/*
 * Copyright (c) 2024 Paul Ostrowski. All rights reserved.
 *
 * This software is the proprietary information of Paul Ostrowski.
 * Unauthorized copying, modification, or distribution of this code, via any medium, is strictly prohibited.
 * This code is proprietary and confidential.
 *
 * For licensing inquiries, contact Paul Ostrowski at postrowski@gmail.com.
*/

import Class from "model/character/Class";
import Section from "model/abilities/Section";
import Spell from "model/stats/Spell";
import ClassService from "services/ClassService";

export class ClassDetails {
    classInfo: Class | null = null;
    classLevel: number = 0;
    archetype: Class | null = null;
    optionsSelectedArrayByOptionNameMap = new Map<string, string[]>();
    spellbookSpellsArrayBySpellLevel: Spell[][] = [[], [], [], [], [], [], [], [], [], []];
    spellsPreparedArrayBySpellLevel: Spell[][] = [[], [], [], [], [], [], [], [], [], []];
    combinedSections: Section[] = []; // the combined classInfo.section and archetype.section, filtered for level

    public clone = (): ClassDetails => {
        const copy = new ClassDetails();
        copy.classInfo = this.classInfo;
        copy.classLevel = this.classLevel;
        copy.archetype = this.archetype;
        copy.optionsSelectedArrayByOptionNameMap = this.optionsSelectedArrayByOptionNameMap;
        copy.spellbookSpellsArrayBySpellLevel = this.spellbookSpellsArrayBySpellLevel;
        copy.spellsPreparedArrayBySpellLevel = this.spellsPreparedArrayBySpellLevel;
        copy.combinedSections = this.combinedSections;
        return copy;
    }
    public clear = () => {
        this.classInfo = null;
        this.classLevel = 0;
        this.archetype = null;
        this.optionsSelectedArrayByOptionNameMap.clear();
        this.spellbookSpellsArrayBySpellLevel = [[], [], [], [], [], [], [], [], [], []];
        this.spellsPreparedArrayBySpellLevel = [[], [], [], [], [], [], [], [], [], []];
        this.combinedSections = [];
    }

    private filterSection = (section: Section, indent: string, selectedLevel: number): Section => {
        const newSection: Section = {...section};
        if (indent && indent !== '') {
          newSection.desc = newSection.desc.split('\n').map(o => indent + o).join('\n');
          newSection.name = indent.replace(/\>/,' \u00a0 \u00a0 ') + newSection.name;
        }
        if (this.classInfo) {
            const regex = /as shown in the ([a-z ]*) column of the ([a-z ]*) table/i;
            const match = newSection.desc.match(regex);
            let tableValue = null;
            let key = null;
            if (match && match[2].toLowerCase() == this.classInfo.name.toLowerCase()) {
                key = match[0];
                tableValue = ClassService.getLevelAbility(this.classInfo, this.classLevel, match[1]);
            } else {
                // This is for the 'Unarmored Movement' of Monks:
                const regex2 = /as shown in the ([a-z ]*) table/i;
                const match2 = newSection.desc.match(regex2);
                if (match2 && match2[1].toLowerCase() == this.classInfo.name.toLowerCase()) {
                    key = match2[0];
                    tableValue = ClassService.getLevelAbility(this.classInfo, this.classLevel, section.name);
                }
            }
            if (tableValue && key) {
                newSection.desc = newSection.desc.replace(key, `at level ${this.classLevel} its ${tableValue}`);
            }
            const ragePhrase = 'Once you have raged the number of times shown for your barbarian level in the Rages column of the Barbarian table';
            if (newSection.desc.includes(ragePhrase)) {
                const rages = ClassService.getLevelAbility(this.classInfo, this.classLevel, 'Rages');
                newSection.desc = newSection.desc.replace(ragePhrase, `Once you have raged ${rages} times`);
            }
        }
        newSection.children = newSection.children.map(child => this.filterSection(child, (indent + '>'), selectedLevel));
        return newSection;
    };

    public getSections = (): void => {
        if (this.classInfo) {
          const baseSections = this.classInfo.sections || [];
          const archetypeSections = (this.classLevel >= 3) ? (this.archetype?.sections || []) : [];
          this.combinedSections = [...baseSections, ...archetypeSections]
                                           .filter(section => section.minLevel <= this.classLevel)
                                           .map(section => this.filterSection(section, '', this.classLevel));
        } else {
            this.combinedSections = [];
        }
    };

    private isSpellPresent = (spell: Spell, spellsArray: Spell[]): boolean => {
        for (const spellsKnowable of spellsArray) {
            if (spell.name === spellsKnowable.name) {
                return true;
            }
        }
        return false;
    }

    public filterSpells = (spellsArrayBySpellLevel: Spell[][], spellsKnowableArrayBySpellLevelMap: Map<number, Spell[]>): Spell[][] => {
        const knowableSpellsBySpellLevel: Spell[][] = [[], [], [], [], [], [], [], [], [], []];
        const maxLevel = ClassService.getHighestSpellsLevelKnownForClassLevel(this.classInfo, this.classLevel) ||
                       ClassService.getHighestSpellsLevelKnownForClassLevel(this.archetype, this.classLevel);
        for (let spellLevel = maxLevel; spellLevel >= 0; spellLevel--) {
            if (spellsArrayBySpellLevel[spellLevel]) {
                const spellsKnowableArray = spellsKnowableArrayBySpellLevelMap.get(spellLevel) || [];
                const rejectedSpells = spellsArrayBySpellLevel[spellLevel].filter((spell) => !this.isSpellPresent(spell, spellsKnowableArray));
                knowableSpellsBySpellLevel[spellLevel] = spellsArrayBySpellLevel[spellLevel].filter((spell) => this.isSpellPresent(spell, spellsKnowableArray));
                // If there are any rejectedSpells, they may have come from lower levels. push these all into the lower level, where it will be checked.
                if (spellLevel > 0) {
                    for (const rejectedSpell of rejectedSpells) {
                        spellsArrayBySpellLevel[spellLevel-1].push(rejectedSpell);
                    }
                }
            }
        }
        return knowableSpellsBySpellLevel;
    }

    private validateSpellbook = (spellsBySpellLevelByClass: Map<string, Map<number, Spell[]>>): void => {
        if (this.classInfo?.name !== 'Wizard') {
            this.spellbookSpellsArrayBySpellLevel = [[], [], [], [], [], [], [], [], [], []];
            return;
        }
        if (!spellsBySpellLevelByClass.size) {
            // If spellsBySpellLevelByClass isn't setup yet, don't remove spells from the spellbook yet
            return;
        }
        const wizardspellsKnowableArrayBySpellLevelMap = spellsBySpellLevelByClass.get('wizard') || new Map();
        this.spellbookSpellsArrayBySpellLevel = this.filterSpells(this.spellbookSpellsArrayBySpellLevel, wizardspellsKnowableArrayBySpellLevelMap);
    }

    public validateSpells = (spellsBySpellLevelByClass: Map<string, Map<number, Spell[]>>): void => {
        let spellsKnowableArrayBySpellLevelMap = spellsBySpellLevelByClass.get(this.classInfo?.name.toLowerCase() || '') ||
                                                 spellsBySpellLevelByClass.get(this.archetype?.name.toLowerCase() || '');
        if (!spellsKnowableArrayBySpellLevelMap) {
            if (spellsBySpellLevelByClass.size) {
                // not a caster class, clear the spells prepared:
                this.spellsPreparedArrayBySpellLevel = [[], [], [], [], [], [], [], [], [], []];
            }
            return;
        }
        
        this.validateSpellbook(spellsBySpellLevelByClass);
        if (this.classInfo?.name === 'Wizard') {
            const cantrips = spellsKnowableArrayBySpellLevelMap.get(0) || [];
            spellsKnowableArrayBySpellLevelMap = new Map();
            spellsKnowableArrayBySpellLevelMap.set(0, cantrips);
            for (let spellLevel = 1; spellLevel <= 9; spellLevel++) {
                spellsKnowableArrayBySpellLevelMap.set(spellLevel, this.spellbookSpellsArrayBySpellLevel[spellLevel]);
            }
        }

        this.spellsPreparedArrayBySpellLevel = this.filterSpells(this.spellsPreparedArrayBySpellLevel,
                                                                 spellsKnowableArrayBySpellLevelMap);
    }

    public validateSelectedOptions = (): void => {
        const validatedSelectedOptions: Map<string, any> = new Map();
        const sections = this.archetype ? [...this.classInfo?.sections || [], ...this.archetype.sections] : [...this.classInfo?.sections || []];
        for (const sect of sections) {
            const options = [...sect.options];
            if (options) {
                let selectedArray = this.optionsSelectedArrayByOptionNameMap.get(sect.name);
                selectedArray = options.filter(o => selectedArray?.includes(o));
                let optionsCount = 1;
                if (sect.name === 'Fighting Style') {
                    for (const additionalStyle of sections.filter(s => s.name === 'Additional Fighting Style')) {
                        if (additionalStyle.desc === 'At 10th level, you can choose a second option from the Fighting Style class feature.' && this.classLevel >= 10) {
                            optionsCount++;
                        }
                    }
                }
                if (sect.name === 'Metamagic') {
                    optionsCount = 0;
                    if (sect.desc.includes('At 3rd level, you gain the ability to twist your spells to suit your needs. You gain two of the following Metamagic options of your choice.')) {
                        if (this.classLevel >= 3) {
                            optionsCount = 2;
                            if (sect.desc.includes('You gain another one at 10th and 17th level.') && this.classLevel >= 10) {
                                optionsCount++;
                                if (this.classLevel >= 17) {
                                    optionsCount++;
                                }
                            }
                        }
                    }
                }
                if (sect.name === 'Pact Boon') {
                    optionsCount = 1;
                }
                if (sect.name === 'Eldritch Invocations' && this.classInfo) {
                    const invocationsKnownStr = ClassService.getLevelAbility(this.classInfo, this.classLevel, 'Invocations Known');
                    optionsCount = parseInt(invocationsKnownStr) || 0;
                }
                if (sect.name === 'Wyrmling Blessing') {
                    optionsCount = 1;
                }
                while (selectedArray.length < optionsCount) {
                    selectedArray.push('');
                }
                while (selectedArray.length > optionsCount) {
                    selectedArray.pop();
                }
                validatedSelectedOptions.set(sect.name, selectedArray);
            }
        }
        this.optionsSelectedArrayByOptionNameMap = validatedSelectedOptions;
    }

}
